import { useState } from "react"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { GlassCard } from "./GlassCard"
import { LoadingSpinner } from "./LoadingSpinner"
import { useApexState } from "@/hooks/useApexState"
import { 
  X, 
  Plus, 
  Minus, 
  ShoppingBag,
  CreditCard,
  Truck,
  Shield,
  Gift,
  Crown
} from "lucide-react"

interface CartItem {
  id: string
  productId: string
  name: string
  price: number
  image: string
  size: string
  color: string
  quantity: number
  isApexLocked?: boolean
}

interface CartProps {
  isOpen: boolean
  onClose: () => void
}

export function Cart({ isOpen, onClose }: CartProps) {
  const { state, actions } = useApexState()
  const [promoCode, setPromoCode] = useState("")
  const [isCheckingOut, setIsCheckingOut] = useState(false)

  // Mock cart items
  const cartItems: CartItem[] = [
    {
      id: "1",
      productId: "apex-joggers",
      name: "Apex Kinetic Joggers",
      price: 180,
      image: "https://images.unsplash.com/photo-1556905055-8f358a7a47b2?w=150&h=150&fit=crop",
      size: "L",
      color: "Charcoal",
      quantity: 1,
      isApexLocked: true
    },
    {
      id: "2",
      productId: "form-tank",
      name: "Form Training Tank",
      price: 85,
      image: "https://images.unsplash.com/photo-1583900985737-6d0495555783?w=150&h=150&fit=crop",
      size: "M",
      color: "Sage",
      quantity: 2
    }
  ]

  const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0)
  const shipping = 0 // Free shipping for Apex members
  const apexDiscount = state.user?.tier === 'architect' ? subtotal * 0.1 : 0 // 10% architect discount
  const total = subtotal - apexDiscount + shipping

  const tierPoints = Math.floor(total * (state.user?.tier === 'architect' ? 3 : state.user?.tier === 'creator' ? 2 : 1))

  const handleQuantityChange = (itemId: string, newQuantity: number) => {
    // Implementation would update cart state
    console.log('Update quantity:', itemId, newQuantity)
  }

  const handleRemoveItem = (itemId: string) => {
    // Implementation would remove from cart
    console.log('Remove item:', itemId)
  }

  const handleCheckout = async () => {
    setIsCheckingOut(true)
    // Simulate checkout process
    await new Promise(resolve => setTimeout(resolve, 2000))
    setIsCheckingOut(false)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="absolute top-0 right-0 w-full max-w-md h-full">
        <GlassCard className="h-full rounded-none rounded-l-lg">
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <ShoppingBag size={20} className="text-foreground" />
                <h2 className="text-lg font-semibold text-foreground">Cart</h2>
                <Badge className="bg-primary text-black text-xs">
                  {cartItems.reduce((sum, item) => sum + item.quantity, 0)}
                </Badge>
              </div>
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X size={20} />
              </Button>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {cartItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <ShoppingBag size={48} className="text-muted-foreground opacity-50 mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    Your cart is empty
                  </h3>
                  <p className="text-muted-foreground">
                    Add some exclusive items to get started
                  </p>
                  <Button 
                    className="mt-4 bg-primary hover:bg-primary/90 text-black"
                    onClick={onClose}
                  >
                    Continue Shopping
                  </Button>
                </div>
              ) : (
                cartItems.map(item => (
                  <Card key={item.id} className="bg-card/50 border-white/10">
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        <div className="w-16 h-16 rounded-lg overflow-hidden">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-medium text-foreground leading-tight">
                                {item.name}
                              </h4>
                              <p className="text-sm text-muted-foreground">
                                {item.color} • {item.size}
                              </p>
                              {item.isApexLocked && (
                                <Badge className="bg-primary/20 text-primary text-xs mt-1">
                                  <Crown size={8} className="mr-1" />
                                  Apex Exclusive
                                </Badge>
                              )}
                            </div>
                            <button
                              onClick={() => handleRemoveItem(item.id)}
                              className="p-1 hover:bg-white/10 rounded transition-colors"
                            >
                              <X size={16} className="text-muted-foreground" />
                            </button>
                          </div>
                          
                          <div className="flex items-center justify-between mt-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                                disabled={item.quantity <= 1}
                                className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 disabled:opacity-50"
                              >
                                <Minus size={12} />
                              </button>
                              <span className="w-8 text-center text-sm font-medium">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                                className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10"
                              >
                                <Plus size={12} />
                              </button>
                            </div>
                            
                            <p className="text-lg font-semibold text-primary">
                              ${(item.price * item.quantity).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>

            {/* Checkout Section */}
            {cartItems.length > 0 && (
              <div className="border-t border-white/10 p-4 space-y-4">
                {/* Promo Code */}
                <div className="flex gap-2">
                  <Input
                    placeholder="Promo code"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    className="flex-1 bg-input/50 border-white/20"
                  />
                  <Button variant="outline" size="sm">
                    Apply
                  </Button>
                </div>

                {/* Order Summary */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="text-foreground">${subtotal.toFixed(2)}</span>
                  </div>
                  
                  {apexDiscount > 0 && (
                    <div className="flex justify-between text-accent">
                      <span>Architect Discount (10%)</span>
                      <span>-${apexDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Truck size={12} />
                      Shipping
                    </span>
                    <span className="text-accent">FREE</span>
                  </div>
                  
                  <div className="flex justify-between text-base font-semibold pt-2 border-t border-white/10">
                    <span>Total</span>
                    <span className="text-primary">${total.toFixed(2)}</span>
                  </div>
                  
                  <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                    <Gift size={12} />
                    <span>You'll earn {tierPoints} Apex Points</span>
                  </div>
                </div>

                {/* Checkout Button */}
                <Button
                  onClick={handleCheckout}
                  disabled={isCheckingOut}
                  className="w-full bg-primary hover:bg-primary/90 text-black font-semibold h-12"
                >
                  {isCheckingOut ? (
                    <>
                      <LoadingSpinner size="sm" variant="muted" className="mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard size={20} className="mr-2" />
                      Secure Checkout
                    </>
                  )}
                </Button>

                {/* Security Badges */}
                <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Shield size={12} />
                    <span>Secure Payment</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Truck size={12} />
                    <span>Free Returns</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </GlassCard>
      </div>
    </div>
  )
}